// Client-side helpers for importing Microsoft 365 admin center CSV exports (Active users / Licenses).
import Papa from "papaparse";

const key = (h) => String(h || "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();

export const USER_FIELDS = [
  { id: "upn", label: "User principal name", required: true, syn: ["user principal name", "userprincipalname", "upn", "user name", "username", "sign in name", "email", "mail", "primary email", "email address"] },
  { id: "display_name", label: "Display name", syn: ["display name", "displayname", "name", "full name", "user"] },
  { id: "licenses", label: "Licenses", syn: ["licenses", "license", "assigned licenses", "assignedlicenses", "products", "product", "skus", "license names", "licences"] },
  { id: "blocked", label: "Sign-in blocked", syn: ["block credential", "blockcredential", "sign in blocked", "blocked", "signin blocked", "account status"] },
  { id: "enabled", label: "Account enabled", syn: ["account enabled", "accountenabled", "enabled"] },
  { id: "user_type", label: "User type", syn: ["user type", "usertype"] },
  { id: "department", label: "Department", syn: ["department"] },
  { id: "job_title", label: "Job title", syn: ["title", "job title", "jobtitle"] },
  { id: "usage_location", label: "Usage location", syn: ["usage location", "usagelocation"] },
  { id: "last_sign_in", label: "Last sign-in", syn: ["last sign in", "last sign in time", "last sign in date", "lastsignindatetime", "last interactive sign in", "lastsignin", "last signin", "last logon", "last login"] },
  { id: "object_id", label: "Object ID", syn: ["object id", "objectid", "id", "user id"] },
];

export const LICENSE_FIELDS = [
  { id: "product", label: "Product", required: true, syn: ["product", "product name", "name", "license", "license name", "sku", "skupartnumber", "sku part number", "friendlyname", "friendly name", "subscription", "licence"] },
  { id: "total", label: "Total seats", syn: ["total", "total licenses", "totallicenses", "purchased", "enabled", "quantity", "licenses", "total units", "prepaid", "total seats"] },
  { id: "assigned", label: "Assigned seats", syn: ["assigned", "assigned licenses", "assignedlicenses", "consumed", "consumedunits", "consumed units", "used", "in use", "assigned seats"] },
];

/** Guess the header for each field (exact synonym first, then prefix match). */
export const detectMapping = (headers, fields) => {
  const byKey = Object.fromEntries(headers.map((h) => [key(h), h]));
  const mapping = {};
  const used = new Set();
  fields.forEach((f) => {
    const hit = f.syn.map((s) => byKey[s]).find((h) => h && !used.has(h)) || headers.find((h) => !used.has(h) && f.syn.some((s) => key(h).startsWith(s) && s.length > 3));
    mapping[f.id] = hit || "";
    if (hit) used.add(hit);
  });
  return mapping;
};

export const parseCsvFile = (file) =>
  new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: "greedy",
      transformHeader: (h) => String(h || "").replace(/^\uFEFF/, "").trim(),
      complete: (res) => resolve({ headers: (res.meta.fields || []).filter(Boolean), rows: res.data, errors: res.errors.slice(0, 5) }),
      error: reject,
    });
  });

const PLUS_NAMES = new Set(["enterprise mobility + security e3", "enterprise mobility + security e5", "microsoft 365 f5 security + compliance"]);

export const splitLicenses = (cell) => {
  const parts = String(cell || "")
    .split(/\+|;|\|/)
    .map((p) => p.trim())
    .filter(Boolean);
  const out = [];
  for (let i = 0; i < parts.length; i += 1) {
    const merged = i + 1 < parts.length ? `${parts[i]} + ${parts[i + 1]}` : null;
    if (merged && PLUS_NAMES.has(merged.toLowerCase())) {
      out.push(merged);
      i += 1;
    } else out.push(parts[i]);
  }
  return out;
};

const truthy = (v) => /^(true|yes|1|blocked|disabled)$/i.test(String(v || "").trim());
const toIso = (v) => {
  const s = String(v || "").trim();
  if (!s) return null;
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
};

/** Normalize parsed CSV rows into the import payload shape. */
export const toImportRows = (rows, m) =>
  rows
    .map((r) => {
      const upn = String(r[m.upn] || "").trim();
      if (!upn) return null;
      const blocked = m.blocked ? truthy(r[m.blocked]) : m.enabled ? !truthy(r[m.enabled]) && String(r[m.enabled] || "").trim() !== "" : false;
      const type = m.user_type ? String(r[m.user_type] || "").trim().toLowerCase() : "";
      return {
        upn: upn.slice(0, 320),
        display_name: String((m.display_name && r[m.display_name]) || "").trim().slice(0, 256),
        licenses: m.licenses ? splitLicenses(r[m.licenses]).slice(0, 60) : [],
        blocked,
        guest: type ? type === "guest" : null,
        department: (m.department && String(r[m.department] || "").trim().slice(0, 200)) || null,
        job_title: (m.job_title && String(r[m.job_title] || "").trim().slice(0, 200)) || null,
        usage_location: (m.usage_location && String(r[m.usage_location] || "").trim().slice(0, 10)) || null,
        last_sign_in: m.last_sign_in ? toIso(r[m.last_sign_in]) : null,
        object_id: (m.object_id && String(r[m.object_id] || "").trim().slice(0, 64)) || null,
      };
    })
    .filter(Boolean);

const num = (v) => {
  const n = parseInt(String(v || "").replace(/[^0-9-]/g, ""), 10);
  return Number.isNaN(n) ? 0 : Math.max(n, 0);
};

export const toImportLicenses = (rows, m) =>
  rows
    .map((r) => ({ product: String(r[m.product] || "").trim().slice(0, 200), total: m.total ? num(r[m.total]) : 0, assigned: m.assigned ? num(r[m.assigned]) : 0 }))
    .filter((l) => l.product);

export const distinctProducts = (rows, licenses) => [...new Set([...rows.flatMap((r) => r.licenses), ...licenses.map((l) => l.product)])];
